import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { ChevronLeft, ChevronRight, Filter, Search } from 'lucide-react';
import {
  ROSTER_PAGE_SIZE_OPTIONS,
  type RosterSortField,
} from '@/components/clients/lib/use-roster-query';
import { ClientClassificationFilterPanel } from '@/components/clients/ClientClassificationFilterPanel';
import type { ClassificationTagId } from '@/lib/clients/classification-tags';
import type { ClientNeedsTag } from '@/lib/types';
import { cn } from '@/lib/utils';

interface ClientRosterToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  page: number;
  totalPages: number;
  total: number;
  onPageChange: (page: number) => void;
  activeFilterCount: number;
  classificationFilters: ClassificationTagId[];
  classificationFilterCount: number;
  needsFilters: string[];
  needsOptions: ClientNeedsTag[];
  onToggleClassificationFilter: (tagId: ClassificationTagId) => void;
  onToggleNeedsFilter: (needsKey: string) => void;
  onClearAllFilters: () => void;
  sortField: RosterSortField;
  onSortFieldChange: (field: RosterSortField) => void;
}

export function ClientRosterToolbar({
  search,
  onSearchChange,
  pageSize,
  onPageSizeChange,
  page,
  totalPages,
  total,
  onPageChange,
  activeFilterCount,
  classificationFilters,
  classificationFilterCount,
  needsFilters,
  needsOptions,
  onToggleClassificationFilter,
  onToggleNeedsFilter,
  onClearAllFilters,
  sortField,
  onSortFieldChange,
}: ClientRosterToolbarProps) {
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);

  return (
    <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-3">
      {/* Search + Filters share one row on mobile, then flow inline from lg. */}
      <div className="flex items-center gap-2 lg:contents">
        <div className="relative flex-1 min-w-0 lg:max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          data-testid="roster-search"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search by name, email, or ID…"
          aria-label="Search clients by name, email, or ID"
          className="pl-9"
        />
      </div>

      <div className="flex justify-center lg:flex-none">
        {/* Desktop: anchored popover. */}
        <div className="hidden md:block">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={cn('h-9 gap-2', activeFilterCount > 0 && 'border-primary/50')}
                data-testid="roster-filters-trigger"
              >
                <Filter className="h-4 w-4" />
                <span>Filters</span>
                {activeFilterCount > 0 && (
                  <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-xs">
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="center"
              sideOffset={8}
              className="w-[min(calc(100vw-2rem),20rem)] p-0 overflow-hidden"
            >
              <ClientClassificationFilterPanel
                selected={classificationFilters}
                classificationFilterCount={classificationFilterCount}
                needsFilters={needsFilters}
                needsOptions={needsOptions}
                onToggle={onToggleClassificationFilter}
                onToggleNeedsFilter={onToggleNeedsFilter}
                onClearAll={onClearAllFilters}
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Mobile: compact trigger opens a thumb-reachable bottom sheet. */}
        <div className="md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className={cn(
                  'h-11 shrink-0 gap-2 rounded-lg',
                  activeFilterCount > 0 && 'border-primary/50'
                )}
                data-testid="roster-filters-trigger-mobile"
              >
                <Filter className="h-4 w-4" />
                <span>Filters</span>
                {activeFilterCount > 0 && (
                  <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-xs">
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent
              side="bottom"
              className="max-h-[85vh] overflow-y-auto p-0"
              hideCloseButton
            >
              <SheetHeader className="sr-only">
                <SheetTitle>Classification filters</SheetTitle>
              </SheetHeader>
              <ClientClassificationFilterPanel
                selected={classificationFilters}
                classificationFilterCount={classificationFilterCount}
                needsFilters={needsFilters}
                needsOptions={needsOptions}
                onToggle={onToggleClassificationFilter}
                onToggleNeedsFilter={onToggleNeedsFilter}
                onClearAll={onClearAllFilters}
              />
            </SheetContent>
          </Sheet>
        </div>
      </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 lg:ml-auto lg:justify-end">
        <Select value={sortField} onValueChange={(v) => onSortFieldChange(v as RosterSortField)}>
          <SelectTrigger className="h-9 flex-1 sm:w-[150px] sm:flex-none">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="attention">Needs attention</SelectItem>
            <SelectItem value="name">Name</SelectItem>
            <SelectItem value="email">Email</SelectItem>
            <SelectItem value="createdAt">Added</SelectItem>
          </SelectContent>
        </Select>

        <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
          <SelectTrigger className="hidden h-9 w-[100px] sm:flex">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ROSTER_PAGE_SIZE_OPTIONS.map((size) => (
              <SelectItem key={size} value={String(size)}>
                {size} / page
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <span
            className="whitespace-nowrap text-xs sm:text-sm"
            aria-live="polite"
            aria-atomic="true"
          >
            <span className="sr-only">Showing </span>
            {rangeStart}–{rangeEnd} of {total}
            <span className="sr-only"> clients</span>
          </span>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-11 w-11 md:h-9 md:w-9"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-11 w-11 md:h-9 md:w-9"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            aria-label="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
